import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const STATUSES = ["open", "in_progress", "blocked", "done"];

function healthChip(h) {
  const cls = h === "red" ? "chip-red" : h === "yellow" ? "chip-yellow" : "chip-green";
  return <span className={`chip ${cls}`}><span className={`dot dot-${h}`} />{h?.toUpperCase()}</span>;
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", workstream: "operations", owner: "Mark Geist",
    deadline: "", expected_result: "", next_action: "", status: "open",
    mission_impact: 5, revenue_impact: 5, urgency: 5,
  });

  const load = async () => {
    setLoading(true);
    try { setTasks((await api.get("/tasks")).data); }
    catch { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...form, deadline: form.deadline || null });
      toast.success("Task created");
      setShowForm(false);
      setForm({ ...form, title: "", expected_result: "", next_action: "" });
      load();
    } catch { toast.error("Create failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    load();
  };

  const del = async (id) => {
    await api.delete(`/tasks/${id}`);
    toast.success("Deleted");
    load();
  };

  const sorted = [...tasks].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <PageHeader
        overline="Operations"
        title="Tasks"
        subtitle="Every commitment. Every deadline. Sorted by priority score."
        actions={
          <button data-testid="new-task-btn" onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center gap-1">
            <Plus size={14} weight="bold" /> New task
          </button>
        }
      />

      {showForm && (
        <div className="oz-card p-5 mb-6 animate-fade-in" data-testid="new-task-form">
          <div className="flex justify-between items-start mb-3">
            <div className="overline">New task</div>
            <button onClick={() => setShowForm(false)}><X size={16} /></button>
          </div>
          <form onSubmit={create} className="grid md:grid-cols-2 gap-3">
            <input data-testid="task-title" required placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-2 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Workstream" value={form.workstream} onChange={(e) => setForm({ ...form, workstream: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm font-mono" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Expected result" value={form.expected_result} onChange={(e) => setForm({ ...form, expected_result: e.target.value })} className="col-span-2 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Next action" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="col-span-2 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <div className="col-span-2 grid grid-cols-3 gap-3">
              <label className="text-xs text-[color:var(--oz-text-dim)]">Mission {form.mission_impact}
                <input type="range" min="1" max="10" value={form.mission_impact} onChange={(e) => setForm({ ...form, mission_impact: +e.target.value })} className="w-full" />
              </label>
              <label className="text-xs text-[color:var(--oz-text-dim)]">Revenue {form.revenue_impact}
                <input type="range" min="1" max="10" value={form.revenue_impact} onChange={(e) => setForm({ ...form, revenue_impact: +e.target.value })} className="w-full" />
              </label>
              <label className="text-xs text-[color:var(--oz-text-dim)]">Urgency {form.urgency}
                <input type="range" min="1" max="10" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: +e.target.value })} className="w-full" />
              </label>
            </div>
            <div className="col-span-2 flex gap-2">
              <button data-testid="task-submit" type="submit" className="btn-primary text-sm">Create</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="oz-card overflow-hidden" data-testid="tasks-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[color:var(--oz-border)] text-[color:var(--oz-text-muted)]">
              <th className="text-left px-4 py-2 overline">Prio</th>
              <th className="text-left px-4 py-2 overline">Health</th>
              <th className="text-left px-4 py-2 overline">Title</th>
              <th className="text-left px-4 py-2 overline">Workstream</th>
              <th className="text-left px-4 py-2 overline">Owner</th>
              <th className="text-left px-4 py-2 overline">Deadline</th>
              <th className="text-left px-4 py-2 overline">Status</th>
              <th className="text-left px-4 py-2 overline"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="p-6 text-center text-[color:var(--oz-text-dim)]">Loading…</td></tr>}
            {!loading && sorted.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-[color:var(--oz-text-muted)]">No tasks yet.</td></tr>}
            {sorted.map((t) => (
              <tr key={t.id} className="oz-row">
                <td className="px-4 py-2.5 font-mono text-xs" style={{ color: t.priority_class === "critical" ? "#fca5a5" : t.priority_class === "high" ? "var(--oz-gold)" : "var(--oz-text-dim)" }}>
                  {t.priority_score?.toFixed(1)}
                </td>
                <td className="px-4 py-2.5">{healthChip(t.health)}</td>
                <td className="px-4 py-2.5 max-w-xs truncate">{t.title}</td>
                <td className="px-4 py-2.5 text-xs text-[color:var(--oz-text-dim)] font-mono">{t.workstream}</td>
                <td className="px-4 py-2.5 text-xs text-[color:var(--oz-text-dim)]">{t.owner}</td>
                <td className="px-4 py-2.5 text-xs font-mono text-[color:var(--oz-text-dim)]">{t.deadline ? new Date(t.deadline).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2.5">
                  <select value={t.status} onChange={(e) => updateStatus(t.id, e.target.value)} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-2 py-1 text-xs rounded-sm font-mono">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => del(t.id)} className="text-[color:var(--oz-text-muted)] hover:text-[color:var(--oz-red)] transition-colors" data-testid={`delete-task-${t.id}`}>
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
